<template>
  <div class="ordenes-compras">

    <!-- Cabecera -->
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <h5 class="ordenes-titulo mb-0">
        <i class="bi bi-bag-check me-2"></i>Compras Registradas
      </h5>
      <router-link v-if="puedeCrear" to="/compras/registrar" class="btn btn-sm btn-primary">
        <i class="bi bi-plus-lg me-1"></i>Nueva Compra
      </router-link>
    </div>

    <!-- Filtros -->
    <div class="ordenes-panel mb-3">
      <div class="ordenes-panel__cuerpo">
        <div class="row g-2 align-items-end">
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Desde</label>
            <input type="date" class="form-control form-control-sm" v-model="filtros.desde" @change="cargar" />
          </div>
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Hasta</label>
            <input type="date" class="form-control form-control-sm" v-model="filtros.hasta" @change="cargar" />
          </div>
          <div class="col-sm-4 col-md-4">
            <label class="form-label form-label-sm mb-1">Proveedor</label>
            <input type="text" class="form-control form-control-sm" v-model="filtros.proveedor" placeholder="Nombre o RUC" @keyup.enter="cargar" />
          </div>
          <div class="col-auto d-flex gap-1">
            <button class="btn btn-sm btn-outline-secondary" @click="cargar">
              <i class="bi bi-search me-1"></i>Buscar
            </button>
            <button class="btn btn-sm btn-link ms-1" style="color:var(--text-muted)" @click="limpiarFiltros">Limpiar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Alerta -->
    <div v-if="alerta.mensaje" class="ordenes-alerta d-flex align-items-center gap-2 mb-3" :class="alerta.tipo === 'error' ? 'ordenes-alerta--error' : 'ordenes-alerta--exito'">
      <i :class="alerta.tipo === 'error' ? 'bi bi-x-circle-fill' : 'bi bi-check-circle-fill'"></i>
      <span>{{ alerta.mensaje }}</span>
    </div>

    <!-- Tabla -->
    <div class="ordenes-panel">
      <!-- Estado: cargando -->
      <div v-if="cargando" class="ordenes-estado-vacio">
        <span class="spinner-border spinner-border-sm me-2"></span>Cargando compras…
      </div>

      <!-- Estado: vacío -->
      <div v-else-if="compras.length === 0" class="ordenes-estado-vacio">
        <i class="bi bi-inbox fs-2 d-block mb-2"></i>No se encontraron compras con los filtros aplicados.
      </div>

      <!-- Tabla principal -->
      <div v-else class="table-responsive">
        <table class="ordenes-tabla">
          <thead>
            <tr>
              <th style="width:36px"></th>
              <th>Nº Factura</th>
              <th>Proveedor</th>
              <th>Fecha</th>
              <th class="text-end">Subtotal</th>
              <th class="text-end">IVA</th>
              <th class="text-end">Total</th>
              <th>Origen</th>
              <th class="text-center">Productos</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="c in compras" :key="c.id">
              <!-- Fila principal -->
              <tr
                class="ordenes-fila"
                :class="{ 'ordenes-fila--activa': expandido === c.id }"
                @click="toggleDetalle(c.id)"
              >
                <td class="text-center">
                  <i :class="expandido === c.id ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="ordenes-fila__chevron"></i>
                </td>
                <td class="font-monospace ordenes-td--mono fw-semibold">{{ c.numero_factura }}</td>
                <td>
                  <div class="fw-semibold text-truncate ordenes-proveedor-nombre" :title="c.proveedor_nombre">{{ c.proveedor_nombre }}</div>
                  <div class="font-monospace ordenes-proveedor-ruc">{{ c.proveedor_ruc }}</div>
                </td>
                <td class="ordenes-td--sm">{{ formatFecha(c.fecha_emision) }}</td>
                <td class="text-end ordenes-td--sm">${{ fmt(c.subtotal) }}</td>
                <td class="text-end ordenes-td--sm">${{ fmt(c.iva) }}</td>
                <td class="text-end fw-bold ordenes-td--total">${{ fmt(c.total) }}</td>
                <td>
                  <span class="badge" :class="c.tipo_ingreso === 'xml_sri' ? 'bg-success' : 'bg-secondary'">
                    <i :class="c.tipo_ingreso === 'xml_sri' ? 'bi bi-file-earmark-code' : 'bi bi-pencil'" class="me-1"></i>
                    {{ c.tipo_ingreso === 'xml_sri' ? 'XML' : 'Manual' }}
                  </span>
                </td>
                <td class="text-center">
                  <span v-if="detalles[c.id]" class="ordenes-badge-vinc">
                    {{ detalles[c.id].filter(d => d.producto_id).length }}/{{ detalles[c.id].length }}
                    <span class="ordenes-badge-vinc__label">vinc.</span>
                  </span>
                  <span v-else-if="cargandoDetalle === c.id" class="spinner-border spinner-border-sm" style="color:var(--text-muted)"></span>
                </td>
              </tr>

              <!-- Fila de detalle expandida -->
              <tr v-if="expandido === c.id" class="ordenes-detalle-fila">
                <td colspan="9" class="p-0">
                  <div class="ordenes-detalle">
                    <div v-if="cargandoDetalle === c.id" class="ordenes-estado-vacio py-3">
                      <span class="spinner-border spinner-border-sm me-2"></span>Cargando detalle…
                    </div>
                    <div v-else-if="detalles[c.id]">
                      <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                        <span class="ordenes-detalle__titulo">
                          <i class="bi bi-list-ul me-1"></i>Productos de esta factura
                        </span>
                        <div class="d-flex align-items-center gap-2">
                          <span class="ordenes-detalle__contador">
                            <span style="color:var(--accent-success)">{{ detalles[c.id].filter(d => d.producto_id).length }} vinculados</span>
                            · {{ detalles[c.id].filter(d => !d.producto_id).length }} sin vincular
                          </span>
                          <button v-if="puedeEditar" class="btn btn-sm btn-outline-secondary" @click.stop="abrirEditar(c)">
                            <i class="bi bi-pencil me-1"></i>Editar
                          </button>
                          <button v-if="puedeEditar" class="btn btn-sm btn-outline-warning" @click.stop="abrirNC(c)">
                            <i class="bi bi-receipt-cutoff me-1"></i>Nota de Crédito
                          </button>
                          <button v-if="puedeEliminar" class="btn btn-sm btn-outline-danger" @click.stop="abrirEliminar(c)">
                            <i class="bi bi-trash me-1"></i>Eliminar
                          </button>
                        </div>
                      </div>
                      <div v-if="notasCredito[c.id] && notasCredito[c.id].length" class="ordenes-nc-lista mb-2">
                        <div v-for="nc in notasCredito[c.id]" :key="nc.id" class="ordenes-nc-item">
                          <i class="bi bi-receipt-cutoff me-1"></i>
                          NC {{ nc.numero_nota_credito }} ({{ formatFecha(nc.fecha_nota_credito) }}) —
                          {{ nc.tipo === 'completa' ? 'Completa' : nc.tipo === 'parcial' ? 'Parcial' : 'Solo costo' }}
                          <span v-if="nc.total_revertido">· revertido ${{ fmt(nc.total_revertido) }}</span>
                        </div>
                      </div>
                      <div class="table-responsive">
                        <table class="ordenes-subtabla">
                          <thead>
                            <tr>
                              <th>Código</th>
                              <th>Descripción</th>
                              <th class="text-end" style="width:60px">Cant.</th>
                              <th class="text-end" style="width:90px">Costo u.</th>
                              <th class="text-end" style="width:90px">Total</th>
                              <th style="width:200px">Producto en inventario</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="d in detalles[c.id]" :key="d.id" class="ordenes-subtabla__fila" :class="{ 'ordenes-subtabla__fila--vinculada': d.producto_id }">
                              <td class="font-monospace" style="font-size:0.75rem;color:var(--text-muted)">{{ d.codigo_proveedor || '—' }}</td>
                              <td style="color:var(--text-primary)">{{ d.descripcion }}</td>
                              <td class="text-end" style="color:var(--text-secondary)">{{ d.cantidad }}</td>
                              <td class="text-end" style="color:var(--text-secondary)">${{ fmt(d.costo_unitario) }}</td>
                              <td class="text-end fw-semibold" style="color:var(--text-primary)">${{ fmt(d.total) }}</td>
                              <td>
                                <span v-if="d.producto_id" class="d-flex align-items-center gap-1">
                                  <i class="bi bi-check-circle-fill" style="color:var(--accent-success);font-size:.8rem"></i>
                                  <span class="fw-semibold" style="font-size:.82rem;color:var(--text-primary)">{{ d.nombre_producto || '—' }}</span>
                                  <span v-if="d.codigo_principal" class="font-monospace" style="font-size:0.7rem;color:var(--text-muted)">{{ d.codigo_principal }}</span>
                                </span>
                                <span v-else class="d-flex align-items-center gap-1" style="color:var(--text-muted);font-size:.82rem">
                                  <i class="bi bi-dash-circle" style="font-size:.8rem"></i>Sin vincular
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="!cargando && total > limit" class="ordenes-paginacion">
        <span>Mostrando {{ offset + 1 }}–{{ Math.min(offset + compras.length, total) }} de {{ total }}</span>
        <div class="d-flex gap-1">
          <button class="btn btn-sm btn-outline-secondary" :disabled="offset === 0" @click="paginar(-1)">
            <i class="bi bi-chevron-left"></i>
          </button>
          <button class="btn btn-sm btn-outline-secondary" :disabled="offset + limit >= total" @click="paginar(1)">
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Eliminar compra -->
    <div v-if="modalEliminar.compra" class="ordenes-modal-fondo" @click.self="cerrarEliminar">
      <div class="ordenes-modal">
        <div class="ordenes-modal__header">
          <h6 class="mb-0"><i class="bi bi-exclamation-triangle-fill me-2" style="color:var(--accent-danger)"></i>Eliminar compra {{ modalEliminar.compra.numero_factura }}</h6>
          <button class="btn-close" @click="cerrarEliminar"></button>
        </div>
        <div class="ordenes-modal__body">
          <div v-if="modalEliminar.cargando" class="text-center py-3">
            <span class="spinner-border spinner-border-sm"></span>
          </div>
          <template v-else>
            <p>{{ modalEliminar.mensaje }}</p>
            <div v-if="modalEliminar.impacto && modalEliminar.impacto.length" class="ordenes-impacto">
              <div class="ordenes-impacto__titulo">Productos afectados:</div>
              <div v-for="i in modalEliminar.impacto" :key="i.producto_id" class="ordenes-impacto__fila">
                <span>{{ i.nombre }}</span>
                <span :class="i.resultante < 0 ? 'text-danger' : 'text-warning'">
                  stock actual {{ i.disponible }} → quedaría en {{ i.resultante }}
                </span>
              </div>
            </div>
          </template>
          <div v-if="modalEliminar.error" class="ordenes-alerta ordenes-alerta--error mt-2">{{ modalEliminar.error }}</div>
        </div>
        <div class="ordenes-modal__footer">
          <button class="btn btn-sm btn-secondary" @click="cerrarEliminar">Cancelar</button>
          <button class="btn btn-sm btn-danger" :disabled="modalEliminar.cargando || modalEliminar.enviando" @click="confirmarEliminar">
            <span v-if="modalEliminar.enviando" class="spinner-border spinner-border-sm me-1"></span>
            Eliminar de todas formas
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Editar datos generales -->
    <div v-if="modalEditar.compra" class="ordenes-modal-fondo" @click.self="cerrarEditar">
      <div class="ordenes-modal">
        <div class="ordenes-modal__header">
          <h6 class="mb-0"><i class="bi bi-pencil me-2"></i>Editar compra {{ modalEditar.compra.numero_factura }}</h6>
          <button class="btn-close" @click="cerrarEditar"></button>
        </div>
        <div class="ordenes-modal__body">
          <div class="mb-2">
            <label class="form-label form-label-sm mb-1">Número de factura</label>
            <input type="text" class="form-control form-control-sm" v-model="modalEditar.form.numero_factura" />
          </div>
          <div class="mb-2">
            <label class="form-label form-label-sm mb-1">Fecha de emisión</label>
            <input type="date" class="form-control form-control-sm" v-model="modalEditar.form.fecha_emision" />
          </div>
          <div class="mb-2">
            <label class="form-label form-label-sm mb-1">Observaciones</label>
            <textarea class="form-control form-control-sm" rows="2" v-model="modalEditar.form.observaciones"></textarea>
          </div>
          <div v-if="modalEditar.error" class="ordenes-alerta ordenes-alerta--error mt-2">{{ modalEditar.error }}</div>
        </div>
        <div class="ordenes-modal__footer">
          <button class="btn btn-sm btn-secondary" @click="cerrarEditar">Cancelar</button>
          <button class="btn btn-sm btn-primary" :disabled="modalEditar.enviando" @click="guardarEditar">
            <span v-if="modalEditar.enviando" class="spinner-border spinner-border-sm me-1"></span>Guardar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Nota de Crédito -->
    <div v-if="modalNC.compra" class="ordenes-modal-fondo" @click.self="cerrarNC">
      <div class="ordenes-modal ordenes-modal--ancho">
        <div class="ordenes-modal__header">
          <h6 class="mb-0"><i class="bi bi-receipt-cutoff me-2"></i>Nota de Crédito — {{ modalNC.compra.numero_factura }}</h6>
          <button class="btn-close" @click="cerrarNC"></button>
        </div>
        <div class="ordenes-modal__body">
          <div class="row g-2 mb-2">
            <div class="col-sm-4">
              <label class="form-label form-label-sm mb-1">Nº Nota de Crédito</label>
              <input type="text" class="form-control form-control-sm" v-model="modalNC.form.numero_nota_credito" />
            </div>
            <div class="col-sm-4">
              <label class="form-label form-label-sm mb-1">Fecha</label>
              <input type="date" class="form-control form-control-sm" v-model="modalNC.form.fecha_nota_credito" />
            </div>
            <div class="col-sm-4">
              <label class="form-label form-label-sm mb-1">Tipo</label>
              <select class="form-select form-select-sm" v-model="modalNC.form.tipo">
                <option value="completa">Completa (todos los productos)</option>
                <option value="parcial">Parcial (elegir productos y cantidad)</option>
                <option value="solo_costo">Solo costo (sin tocar cantidad)</option>
              </select>
            </div>
          </div>
          <div class="mb-2">
            <label class="form-label form-label-sm mb-1">Observación</label>
            <input type="text" class="form-control form-control-sm" v-model="modalNC.form.observacion" />
          </div>

          <div v-if="modalNC.form.tipo !== 'completa'" class="ordenes-nc-lineas">
            <div class="ordenes-detalle__titulo mb-1">Productos</div>
            <table class="ordenes-subtabla">
              <thead>
                <tr>
                  <th style="width:30px"></th>
                  <th>Descripción</th>
                  <th class="text-end" style="width:80px">Cant. factura</th>
                  <th class="text-end" style="width:110px">{{ modalNC.form.tipo === 'parcial' ? 'Cant. a revertir' : 'Costo actual' }}</th>
                  <th v-if="modalNC.form.tipo === 'solo_costo'" class="text-end" style="width:110px">Costo nuevo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="linea in modalNC.lineas" :key="linea.id">
                  <td><input type="checkbox" v-model="linea.seleccionada" /></td>
                  <td>{{ linea.descripcion }}</td>
                  <td class="text-end">{{ linea.cantidad }}</td>
                  <td class="text-end">
                    <input
                      v-if="modalNC.form.tipo === 'parcial'"
                      type="number" min="0.0001" :max="linea.cantidad" step="0.0001"
                      class="form-control form-control-sm text-end"
                      v-model.number="linea.cantidad_revertida"
                      :disabled="!linea.seleccionada"
                    />
                    <span v-else>${{ fmt(linea.costo_unitario) }}</span>
                  </td>
                  <td v-if="modalNC.form.tipo === 'solo_costo'" class="text-end">
                    <input
                      type="number" min="0" step="0.0001"
                      class="form-control form-control-sm text-end"
                      v-model.number="linea.costo_nuevo"
                      :disabled="!linea.seleccionada"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="modalNC.error" class="ordenes-alerta ordenes-alerta--error mt-2">{{ modalNC.error }}</div>
          <div v-if="modalNC.faltantes && modalNC.faltantes.length" class="ordenes-impacto mt-2">
            <div class="ordenes-impacto__titulo">Sin stock suficiente para revertir:</div>
            <div v-for="f in modalNC.faltantes" :key="f.producto_id" class="ordenes-impacto__fila">
              <span>{{ f.descripcion }}</span>
              <span class="text-danger">disponible {{ f.disponible }} / requerido {{ f.requerido }}</span>
            </div>
          </div>
        </div>
        <div class="ordenes-modal__footer">
          <button class="btn btn-sm btn-secondary" @click="cerrarNC">Cancelar</button>
          <button class="btn btn-sm btn-warning" :disabled="modalNC.enviando" @click="guardarNC">
            <span v-if="modalNC.enviando" class="spinner-border spinner-border-sm me-1"></span>Registrar Nota de Crédito
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'ClientComprasOrdenes',

  data() {
    return {
      compras:         [],
      detalles:        {}, // { compra_id: [...] }
      notasCredito:    {}, // { compra_id: [...] }
      expandido:       null,
      cargandoDetalle: null,
      cargando:        false,
      total:           0,
      limit:           50,
      offset:          0,
      alerta:          { mensaje: '', tipo: 'error' },
      filtros: {
        desde:     '',
        hasta:     '',
        proveedor: '',
      },
      modalEliminar: { compra: null, cargando: false, enviando: false, impacto: [], mensaje: '', error: '' },
      modalEditar:   { compra: null, enviando: false, error: '', form: { numero_factura: '', fecha_emision: '', observaciones: '' } },
      modalNC:       { compra: null, enviando: false, error: '', faltantes: [], lineas: [], form: { numero_nota_credito: '', fecha_nota_credito: '', tipo: 'completa', observacion: '' } },
    }
  },

  computed: {
    puedeCrear() {
      return this.$store.getters.puedoHacer('/compras/registrar', 'crear')
    },
    puedeEditar() {
      return this.$store.getters.puedoHacer('/compras/ordenes', 'editar')
    },
    puedeEliminar() {
      return this.$store.getters.puedoHacer('/compras/ordenes', 'eliminar')
    },
    fmt() {
      return (v) => Number(v || 0).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
  },

  created() {
    const idParam = this.$route?.params?.id
    this.cargar().then(() => {
      if (idParam) this.toggleDetalle(parseInt(idParam))
    })
  },

  methods: {
    formatFecha(f) {
      if (!f) return '—'
      const [y, m, d] = f.split('-')
      return `${d}/${m}/${y}`
    },

    async cargar() {
      this.cargando = true
      this.alerta   = { mensaje: '', tipo: 'error' }
      try {
        const params = { limit: this.limit, offset: this.offset }
        if (this.filtros.desde) params.fecha_desde = this.filtros.desde
        if (this.filtros.hasta) params.fecha_hasta  = this.filtros.hasta
        if (this.filtros.proveedor) params.proveedor = this.filtros.proveedor
        const { data } = await api.get('/compras', { params })
        this.compras = data.data || []
        this.total   = data.total || this.compras.length
      } catch {
        this.alerta = { mensaje: 'No se pudo cargar el listado de compras.', tipo: 'error' }
      } finally {
        this.cargando = false
      }
    },

    async toggleDetalle(id) {
      if (this.expandido === id) {
        this.expandido = null
        return
      }
      this.expandido = id
      if (this.detalles[id]) return
      this.cargandoDetalle = id
      try {
        const { data } = await api.get(`/compras/${id}`)
        this.detalles = { ...this.detalles, [id]: data.data?.detalles || [] }
      } catch {
        this.detalles = { ...this.detalles, [id]: [] }
      } finally {
        this.cargandoDetalle = null
      }
      this.cargarNotasCredito(id)
    },

    async cargarNotasCredito(id) {
      try {
        const { data } = await api.get(`/compras/${id}/notas-credito`)
        this.notasCredito = { ...this.notasCredito, [id]: data.data || [] }
      } catch {
        this.notasCredito = { ...this.notasCredito, [id]: [] }
      }
    },

    limpiarFiltros() {
      this.filtros = { desde: '', hasta: '', proveedor: '' }
      this.offset  = 0
      this.cargar()
    },

    paginar(dir) {
      this.offset = Math.max(0, this.offset + dir * this.limit)
      this.cargar()
    },

    // ── Eliminar compra ──────────────────────────────────────────────────
    async abrirEliminar(compra) {
      this.modalEliminar = { compra, cargando: true, enviando: false, impacto: [], mensaje: '', error: '' }
      try {
        const { data } = await api.delete(`/compras/${compra.id}`)
        this.modalEliminar = { ...this.modalEliminar, cargando: false, impacto: data.impacto_inventario || [], mensaje: data.mensaje || '' }
      } catch (error) {
        this.modalEliminar = { ...this.modalEliminar, cargando: false, error: error?.response?.data?.error || 'No se pudo verificar el impacto de la eliminación.' }
      }
    },
    cerrarEliminar() {
      this.modalEliminar = { compra: null, cargando: false, enviando: false, impacto: [], mensaje: '', error: '' }
    },
    async confirmarEliminar() {
      const id = this.modalEliminar.compra.id
      this.modalEliminar.enviando = true
      this.modalEliminar.error = ''
      try {
        await api.delete(`/compras/${id}`, { params: { confirmado: true } })
        this.cerrarEliminar()
        this.alerta = { mensaje: 'Compra eliminada correctamente.', tipo: 'exito' }
        if (this.expandido === id) this.expandido = null
        delete this.detalles[id]
        this.cargar()
      } catch (error) {
        this.modalEliminar.error = error?.response?.data?.error || 'No se pudo eliminar la compra.'
      } finally {
        this.modalEliminar.enviando = false
      }
    },

    // ── Editar datos generales ───────────────────────────────────────────
    abrirEditar(compra) {
      this.modalEditar = {
        compra, enviando: false, error: '',
        form: {
          numero_factura: compra.numero_factura || '',
          fecha_emision: compra.fecha_emision || '',
          observaciones: compra.observaciones || '',
        },
      }
    },
    cerrarEditar() {
      this.modalEditar = { compra: null, enviando: false, error: '', form: { numero_factura: '', fecha_emision: '', observaciones: '' } }
    },
    async guardarEditar() {
      const id = this.modalEditar.compra.id
      this.modalEditar.enviando = true
      this.modalEditar.error = ''
      try {
        await api.put(`/compras/${id}`, this.modalEditar.form)
        this.cerrarEditar()
        this.alerta = { mensaje: 'Compra actualizada correctamente.', tipo: 'exito' }
        this.cargar()
      } catch (error) {
        this.modalEditar.error = error?.response?.data?.error || 'No se pudo actualizar la compra.'
      } finally {
        this.modalEditar.enviando = false
      }
    },

    // ── Nota de Crédito ──────────────────────────────────────────────────
    abrirNC(compra) {
      const lineasBase = (this.detalles[compra.id] || []).map(d => ({
        id: d.id,
        descripcion: d.descripcion,
        cantidad: d.cantidad,
        costo_unitario: d.costo_unitario,
        seleccionada: false,
        cantidad_revertida: d.cantidad,
        costo_nuevo: d.costo_unitario,
      }))
      this.modalNC = {
        compra, enviando: false, error: '', faltantes: [], lineas: lineasBase,
        form: { numero_nota_credito: '', fecha_nota_credito: '', tipo: 'completa', observacion: '' },
      }
    },
    cerrarNC() {
      this.modalNC = { compra: null, enviando: false, error: '', faltantes: [], lineas: [], form: { numero_nota_credito: '', fecha_nota_credito: '', tipo: 'completa', observacion: '' } }
    },
    async guardarNC() {
      const id = this.modalNC.compra.id
      const { form, lineas } = this.modalNC
      if (!form.numero_nota_credito || !form.fecha_nota_credito) {
        this.modalNC.error = 'Complete el número y la fecha de la Nota de Crédito.'
        return
      }
      let detalles
      if (form.tipo !== 'completa') {
        const seleccionadas = lineas.filter(l => l.seleccionada)
        if (seleccionadas.length === 0) {
          this.modalNC.error = 'Seleccione al menos un producto.'
          return
        }
        detalles = seleccionadas.map(l => form.tipo === 'parcial'
          ? { compra_detalle_id: l.id, cantidad_revertida: l.cantidad_revertida }
          : { compra_detalle_id: l.id, costo_nuevo: l.costo_nuevo }
        )
      }

      this.modalNC.enviando = true
      this.modalNC.error = ''
      this.modalNC.faltantes = []
      try {
        await api.post(`/compras/${id}/notas-credito`, { ...form, detalles })
        this.cerrarNC()
        this.alerta = { mensaje: 'Nota de Crédito registrada correctamente.', tipo: 'exito' }
        delete this.detalles[id]
        if (this.expandido === id) {
          this.expandido = null
          this.toggleDetalle(id)
        }
        this.cargar()
      } catch (error) {
        this.modalNC.error = error?.response?.data?.error || 'No se pudo registrar la Nota de Crédito.'
        this.modalNC.faltantes = error?.response?.data?.faltantes || []
      } finally {
        this.modalNC.enviando = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Contenedor ── */
.ordenes-compras {
  padding: 1.25rem;
}

.ordenes-titulo {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--primary-color);
}

/* ── Panel / Tarjeta ── */
.ordenes-panel {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.ordenes-panel__cuerpo {
  padding: 0.75rem 1rem;
}

/* ── Alerta ── */
.ordenes-alerta {
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  font-size: 0.875rem;
  border: 1px solid;
}

.ordenes-alerta--error {
  background: var(--accent-danger-light);
  border-color: var(--accent-danger);
  color: var(--accent-danger);
}

.ordenes-alerta--exito {
  background: var(--accent-success-light);
  border-color: var(--accent-success);
  color: var(--accent-success);
}

/* ── Estado vacío / cargando ── */
.ordenes-estado-vacio {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  font-size: 0.9rem;
}

/* ── Tabla principal ── */
.ordenes-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.ordenes-tabla thead tr {
  background: var(--secondary-bg-soft);
  border-bottom: 2px solid var(--border-main);
}

.ordenes-tabla thead th {
  padding: 0.6rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

/* ── Fila principal ── */
.ordenes-fila {
  border-bottom: 1px solid var(--border-main);
  cursor: pointer;
  transition: background 0.1s;
}

.ordenes-fila td {
  padding: 0.55rem 0.75rem;
  vertical-align: middle;
  color: var(--text-primary);
}

.ordenes-fila:hover {
  background: var(--table-hover);
}

.ordenes-fila--activa {
  background: var(--accent-info-light) !important;
  border-left: 3px solid var(--primary-color);
}

.ordenes-fila__chevron {
  font-size: 0.75rem;
  color: var(--text-muted);
}

/* ── Celdas especiales ── */
.ordenes-td--mono  { font-size: 0.82rem; }
.ordenes-td--sm    { font-size: 0.82rem; color: var(--text-secondary); }
.ordenes-td--total { font-size: 0.9rem; color: var(--text-primary); }

.ordenes-proveedor-nombre {
  font-size: 0.85rem;
  max-width: 200px;
  color: var(--text-primary);
}

.ordenes-proveedor-ruc {
  font-size: 0.7rem;
  color: var(--text-muted);
}

/* ── Badge vinculados ── */
.ordenes-badge-vinc {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 20px;
  background: var(--secondary-bg-soft);
  border: 1px solid var(--border-main);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-primary);
}

.ordenes-badge-vinc__label {
  font-size: 0.65rem;
  color: var(--text-muted);
}

/* ── Fila de detalle expandido ── */
.ordenes-detalle-fila {
  border-bottom: 1px solid var(--border-main);
}

.ordenes-detalle {
  background: var(--secondary-bg-soft);
  border-top: 1px solid var(--border-main);
  padding: 1rem 1.25rem;
}

.ordenes-detalle__titulo {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ordenes-detalle__contador {
  font-size: 0.8rem;
  color: var(--text-muted);
}

/* ── Sub-tabla de productos ── */
.ordenes-subtabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 6px;
  overflow: hidden;
}

.ordenes-subtabla thead tr {
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-main);
}

.ordenes-subtabla thead th {
  padding: 0.45rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ordenes-subtabla__fila td {
  padding: 0.4rem 0.65rem;
  border-bottom: 1px solid var(--border-main);
  vertical-align: middle;
}

.ordenes-subtabla__fila:last-child td {
  border-bottom: none;
}

.ordenes-subtabla__fila:hover {
  background: var(--table-hover);
}

.ordenes-subtabla__fila--vinculada td:last-child {
  background: rgba(16, 185, 129, 0.04);
}

/* ── Notas de credito en el detalle ── */
.ordenes-nc-lista {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
}
.ordenes-nc-item {
  font-size: 0.8rem;
  color: var(--text-secondary);
  padding: 0.15rem 0;
}

/* ── Modales ── */
.ordenes-modal-fondo {
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center; z-index: 1060;
}
.ordenes-modal {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  width: 100%; max-width: 480px;
  box-shadow: var(--shadow-md);
}
.ordenes-modal--ancho { max-width: 720px; }
.ordenes-modal__header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.85rem 1.1rem; border-bottom: 1px solid var(--border-main);
  color: var(--text-primary);
}
.ordenes-modal__body { padding: 1rem 1.1rem; color: var(--text-primary); max-height: 65vh; overflow-y: auto; }
.ordenes-modal__footer {
  display: flex; justify-content: flex-end; gap: 0.5rem;
  padding: 0.75rem 1.1rem; border-top: 1px solid var(--border-main);
}
.ordenes-impacto {
  background: var(--accent-danger-light);
  border: 1px solid var(--accent-danger);
  border-radius: 6px;
  padding: 0.6rem 0.85rem;
  font-size: 0.82rem;
}
.ordenes-impacto__titulo { font-weight: 600; margin-bottom: 0.35rem; }
.ordenes-impacto__fila { display: flex; justify-content: space-between; gap: 0.5rem; padding: 0.15rem 0; }
.ordenes-nc-lineas { margin-top: 0.5rem; }

/* ── Paginación ── */
.ordenes-paginacion {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.85rem;
  border-top: 1px solid var(--border-main);
  font-size: 0.82rem;
  color: var(--text-muted);
}
</style>

